import { FC, ReactNode } from 'react';

import { createStaticDataProvider } from '@hh.ru/magritte-common-data-provider';
import { Button, Card, Flex, RenderContentBefore, Select, Text, Trigger, useBreakpoint } from '@hh.ru/magritte-ui';

export interface FilterOption {
    value: string;
    text: string;
}

export interface SingleFilterProps {
    name: string;
    placeholder: string;
    appliedValues: FilterOption[];
    value: FilterOption[];
    isLoading: boolean;
    dataProvider: ReturnType<typeof createStaticDataProvider<FilterOption>>;
    applyLabel: string;
    discardLabel: string;
    dataQaPrefix: string;
    dropWidth?: number;
    forceDropVisibleWithEmptyOptions?: boolean;
    showButtons?: boolean;
    renderContentBefore?: RenderContentBefore;
    onApply: () => void;
    onClear: () => void;
    onChange: (values: FilterOption[]) => void;
    onSelectOption: (values: FilterOption[]) => void;
    onDropClose: () => void;
    onBottomSheetClose: () => void;
    onDropOpen?: () => void;
}

export const SingleFilter: FC<SingleFilterProps> = ({
    name,
    placeholder,
    appliedValues,
    value,
    isLoading,
    dataProvider,
    applyLabel,
    discardLabel,
    dataQaPrefix,
    dropWidth,
    forceDropVisibleWithEmptyOptions,
    showButtons = true,
    renderContentBefore,
    onApply,
    onClear,
    onChange,
    onSelectOption,
    onDropClose,
    onBottomSheetClose,
    onDropOpen,
}) => {
    const { isMobile } = useBreakpoint();

    return (
        <Select
            bottomSheetHeight="content"
            multiple
            name={name}
            type="checkbox"
            value={value}
            widthEqualToActivator={false}
            onApply={onApply}
            onClear={onClear}
            onChange={onChange}
            onSelectOption={onSelectOption}
            onDropClose={onDropClose}
            onDropOpen={onDropOpen}
            onBottomSheetClose={onBottomSheetClose}
            dropWidth={dropWidth}
            forceDropVisibleWithEmptyOptions={forceDropVisibleWithEmptyOptions}
            renderContentBefore={renderContentBefore}
            renderTrigger={(triggerProps) => <Trigger {...triggerProps} onClear={onClear} stretched />}
            triggerProps={{
                applied: appliedValues.length > 0,
                'data-qa': `${dataQaPrefix}-trigger`,
                label: placeholder,
                size: 'medium',
                renderFormattedValue: (_value: ReactNode, index: number) => {
                    if (index === 0) {
                        return (
                            <Flex gap={6}>
                                <Text
                                    typography="label-2-regular"
                                    style={appliedValues.length ? 'primary' : 'tertiary'}
                                >
                                    {placeholder}
                                </Text>
                                {!!appliedValues.length && (
                                    <Text style="secondary" typography="label-2-regular">
                                        {appliedValues.length}
                                    </Text>
                                )}
                            </Flex>
                        );
                    }
                    return '';
                },
            }}
            dataProvider={dataProvider}
            applyChangesButton={
                showButtons ? (
                    <Button
                        stretched
                        mode="primary"
                        style="accent"
                        loading={isLoading}
                        disabled={isLoading}
                        data-qa={`${dataQaPrefix}-apply`}
                    >
                        {applyLabel}
                    </Button>
                ) : undefined
            }
            clearButton={
                showButtons && appliedValues.length > 0 ? (
                    <Button
                        stretched
                        mode="secondary"
                        style="accent"
                        loading={isLoading}
                        disabled={isLoading}
                        data-qa={`${dataQaPrefix}-discard`}
                    >
                        {discardLabel}
                    </Button>
                ) : undefined
            }
            renderButtonsContainer={
                showButtons
                    ? ({ applyChangesButton, clearButton }) => (
                          <Card stretched padding={isMobile ? 16 : 0}>
                              <Flex gap={8} direction={isMobile ? 'column-reverse' : 'row'}>
                                  {clearButton}
                                  {applyChangesButton}
                              </Flex>
                          </Card>
                      )
                    : undefined
            }
        />
    );
};
